import { isEmpty } from 'lodash'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { task } from 'mobx-task/lib/task'
import { combineLatest, Subscription } from 'rxjs'
import { distinctUntilChanged, switchMap } from 'rxjs/operators'
import InfiniteScrollProvider, {
  FetchItems,
} from 'utils/InfiniteScrollProvider'
import { tapAction, toStream } from 'utils/mobx-util'
import { FilterItem, IFilterViewModel } from '../../FiltersViewModel'
import { SearchTextViewModel } from '../../SearchTextViewModel'

export class InfiniteSelectFilter<TKey, TValue>
  implements IFilterViewModel<TKey, TValue[]>
{
  key: TKey
  title: string
  @observable editing = false
  @observable multi = false
  @observable allowSelectAll = false

  /*
   * The default limit of items to display.
   */
  @observable defaultLimit?: number = undefined

  @observable selected: TValue[] = []
  @observable options: FilterItem<TValue>[] = []
  scrollProvider = new InfiniteScrollProvider<FilterItem<TValue>>({
    limit: 20,
    fetchItems: (paging, _, filter) => this.fetchItems(paging, _, filter),
  })

  private subscription = new Subscription()

  private readonly searchText = new SearchTextViewModel()
  private readonly fetchItems: FetchItems<FilterItem<TValue>>
  private readonly searches$ = this.searchText.debouncedValue$.pipe(
    switchMap((searchText) => this.scrollProvider.fetch({ searchText })),
  )

  private readonly cursorUpdates$ = toStream(
    () => this.scrollProvider.cursor,
    true,
  ).pipe(
    distinctUntilChanged(),
    tapAction(() => {
      this.setOptions(this.scrollProvider.items)
    }),
  )

  private readonly combined$ = combineLatest([
    this.searches$,
    this.cursorUpdates$,
  ])

  @computed
  get valid() {
    return !isEmpty(this.value)
  }

  @computed
  get value() {
    return this.selected
  }

  @computed
  get items() {
    return this.scrollProvider.items
  }

  constructor(
    key: TKey,
    title: string,
    fetchItems: FetchItems<FilterItem<TValue>>,
    multi?: boolean,
    allowSelectAll?: boolean,
    defaultLimit?: number,
  ) {
    makeObservable(this)

    this.key = key
    this.title = title
    this.multi = multi ?? false
    this.allowSelectAll = allowSelectAll ?? false
    this.defaultLimit = defaultLimit
    this.selected = []
    this.options = []
    this.fetchItems = fetchItems
  }

  activate = task(async () => {
    await this.scrollProvider.fetch()
    this.subscription = this.combined$.subscribe()
  })

  deactivate() {
    this.subscription.unsubscribe()
  }

  deserialize(value: unknown): void {
    try {
      if (typeof value === 'string') {
        this.options = JSON.parse(value)
        this.selected = this.options.map((o) => o.value)
      }
    } catch (error) {
      this.clearValue()
    }
  }

  serialize() {
    return JSON.stringify(
      this.options.filter((o) => this.selected.includes(o.value)),
    )
  }

  toString(limit?: number) {
    let items = this.selected.map((v) => this.optionsMap.get(v))

    // use the limit that was passed otherwise use the default limit
    const max = limit ?? this.defaultLimit

    if (max && items.length > max) {
      items = items.slice(0, max - 1)

      const otherItemsCount = this.selected.length - items.length
      items.push(`${max > 1 ? '+' : ''}${otherItemsCount} selected`)
    }

    return items.join(', ')
  }

  @computed
  get optionsMap() {
    return new Map(this.options.map((o) => [o.value, o.title]))
  }

  @action.bound
  startEditing(): void {
    this.editing = true
  }

  @action.bound
  stopEditing(): void {
    this.editing = false
  }

  @action.bound
  setOptions(options: FilterItem<TValue>[]) {
    this.options = options
  }

  @action.bound
  changeSearchText(text: string) {
    this.searchText.change(text)
  }

  @action.bound
  toggle(val: TValue) {
    if (this.multi) {
      this.change(
        this.isSelected(val)
          ? this.selected.filter((s) => s !== val)
          : [...this.selected, val],
      )
      return
    }

    if (!this.isSelected(val)) {
      this.selected = [val]
    }
  }

  isSelected(val: TValue) {
    return this.selected.some((s) => s === val)
  }

  @action.bound
  change(val: TValue[]) {
    this.selected = val
  }

  @action.bound
  clearValue(): void {
    this.selected = []
  }

  async selectAll() {
    while (!this.scrollProvider.isDone) {
      await this.scrollProvider.more()
    }

    runInAction(() => {
      this.selected = this.items.map((x) => x.value)
    })
  }
}
